/**
 * Zustand store for managing saved sessions
 *
 * Persists session data to localStorage under 'dicecapture-sessions'.
 * Includes migration logic to convert existing endpoints and old session formats.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { generateSessionId, createInitialSessionMetadata, type SavedSession, type SessionConfig, type SessionMetadata } from '@/types/session';

const SERVICE_TAG = '[SESSIONS]';

/**
 * Safely access a nested string property from an untyped endpoint object
 *
 * @param obj - The root object to access
 * @param keys - The path of keys to traverse
 * @returns The string value at the path, or empty string if not found
 */
function getString(obj: unknown, ...keys: string[]): string {
  let current: unknown = obj;
  for (const key of keys) {
    if (current !== null && current !== undefined && typeof current === 'object') {
      current = (current as Record<string, unknown>)[key];
    } else {
      return '';
    }
  }
  return typeof current === 'string' ? current : '';
}

/**
 * Migrate old session configs (StarterSessionConfig/PlayerSessionConfig) to unified SessionConfig
 *
 * Detects old format by presence of 'role' field on config.
 * - role:'starter' -> playerName from displayName
 * - role:'player' -> playerName kept as-is
 *
 * @param sessions - Array of sessions that may contain old config format
 * @returns Array of sessions with unified SessionConfig
 */
function migrateSessionsToV2(sessions: SavedSession[]): SavedSession[] {
  let migrated = 0;
  const result = sessions.map(session => {
    const oldConfig = session.config as unknown as Record<string, unknown>;

    if (!oldConfig.role) {
      return session;
    }

    migrated++;
    const sessionCode = (oldConfig.sessionCode as string) || '';
    const playerName = oldConfig.role === 'starter'
      ? (oldConfig.displayName as string) || ''
      : (oldConfig.playerName as string) || '';

    return {
      ...session,
      config: { sessionCode, playerName } as SessionConfig
    };
  });

  if (migrated > 0) {
    console.info(`${SERVICE_TAG} V2 migration: converted ${migrated} sessions to unified format`);
  }

  return result;
}

interface SessionsState {
  sessions: SavedSession[];
  migrationComplete: boolean;
  v2MigrationComplete: boolean;
  lastConnectedSessionId: string | null;
  /** Session IDs the user wants to keep connected. Persisted to localStorage. */
  autoConnectSessionIds: string[];

  addSession: (config: SessionConfig) => string;
  updateSession: (id: string, updates: Partial<SavedSession>) => void;
  deleteSession: (id: string) => void;
  getSession: (id: string) => SavedSession | undefined;
  markAsUsed: (id: string) => void;
  setLastConnectedSession: (sessionId: string | null) => void;
  /** Mark a session for auto-connect on app start / page refresh */
  enableAutoConnect: (sessionId: string) => void;
  /** Remove a session from auto-connect (user explicitly disconnected) */
  disableAutoConnect: (sessionId: string) => void;
}

/**
 * Migrate existing endpoints data to sessions format
 *
 * Reads from 'dicecapture-endpoints' localStorage, converts each endpoint
 * to the appropriate session type, and creates a backup before migration.
 *
 * @returns Array of migrated SavedSession objects
 */
function migrateEndpointsToSessions(): SavedSession[] {
  try {
    const endpointsData = localStorage.getItem('dicecapture-endpoints');
    if (!endpointsData) {
      console.info(`${SERVICE_TAG} No endpoints data found, skipping migration`);
      return [];
    }

    // Create backup before migration
    localStorage.setItem('dicecapture-endpoints-backup', endpointsData);
    console.info(`${SERVICE_TAG} Created endpoints backup`);

    const parsed = JSON.parse(endpointsData);
    const endpoints = parsed.state?.endpoints || [];

    if (!Array.isArray(endpoints) || endpoints.length === 0) {
      console.info(`${SERVICE_TAG} No endpoints to migrate`);
      return [];
    }

    console.info(`${SERVICE_TAG} Migrating ${endpoints.length} endpoints to sessions`);

    const migratedSessions: SavedSession[] = endpoints.map((ep: unknown) => {
      const metadata: SessionMetadata = {
        createdAt: getString(ep, 'metadata', 'createdAt') || new Date().toISOString(),
        lastUsedAt: getString(ep, 'metadata', 'lastUsedAt') || new Date().toISOString()
      };

      const epObj = ep as Record<string, unknown>;

      if (epObj.type === 'player') {
        return {
          id: generateSessionId(),
          config: {
            sessionCode: getString(ep, 'credentials', 'sessionCode'),
            playerName: getString(ep, 'credentials', 'playerName')
          },
          metadata
        };
      }

      // standalone, foundry, discord -> session with session code
      // Read from legacy credential fields (roomCode) or new field (sessionCode)
      const migratedSessionCode = getString(ep, 'credentials', 'sessionCode') || getString(ep, 'credentials', 'roomCode') || getString(ep, 'credentials', 'channelId');
      const playerName = getString(ep, 'credentials', 'hostName') || getString(ep, 'credentials', 'playerName') || getString(ep, 'name') || 'Unknown';

      return {
        id: generateSessionId(),
        config: {
          sessionCode: migratedSessionCode,
          playerName
        },
        metadata
      };
    });

    console.info(`${SERVICE_TAG} Successfully migrated ${migratedSessions.length} sessions`);
    return migratedSessions;
  } catch (error) {
    console.error(`${SERVICE_TAG} Migration failed:`, {
      error: error instanceof Error ? error.message : 'Unknown error'
    });
    return [];
  }
}

export const useSessionsStore = create<SessionsState>()(
  persist(
    (set, get) => ({
      sessions: [],
      migrationComplete: false,
      v2MigrationComplete: false,
      lastConnectedSessionId: null,
      autoConnectSessionIds: [],

      addSession: (config) => {
        const code = config.sessionCode.toUpperCase();
        const existing = get().sessions.find(
          (s) => s.config.sessionCode.toUpperCase() === code
        );
        if (existing) {
          console.warn(`${SERVICE_TAG} Duplicate session code rejected`, { sessionCode: code });
          throw new Error(`A session with code "${code}" already exists`);
        }

        const id = generateSessionId();
        const metadata = createInitialSessionMetadata();

        const newSession: SavedSession = {
          id,
          config,
          metadata
        };

        console.info(`${SERVICE_TAG} Adding session`, { id, sessionCode: code });
        set((state) => ({
          sessions: [...state.sessions, newSession]
        }));

        return id;
      },

      updateSession: (id, updates) => {
        console.info(`${SERVICE_TAG} Updating session`, { id, updates: Object.keys(updates) });
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === id ? { ...s, ...updates } : s
          )
        }));
      },

      deleteSession: (id) => {
        console.info(`${SERVICE_TAG} Deleting session`, { id });
        set((state) => ({
          sessions: state.sessions.filter((s) => s.id !== id)
        }));
      },

      getSession: (id) => {
        return get().sessions.find((s) => s.id === id);
      },

      markAsUsed: (id) => {
        const now = new Date().toISOString();
        console.info(`${SERVICE_TAG} Marking session as used`, { id });
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === id
              ? {
                ...s,
                metadata: {
                  ...s.metadata,
                  lastUsedAt: now
                }
              }
              : s
          )
        }));
      },

      setLastConnectedSession: (sessionId) => {
        console.info(`${SERVICE_TAG} Setting last connected session`, { sessionId });
        set({ lastConnectedSessionId: sessionId });
      },

      enableAutoConnect: (sessionId) => {
        set((state) => {
          if (state.autoConnectSessionIds.includes(sessionId)) return state;
          console.info(`${SERVICE_TAG} Enabling auto-connect`, { sessionId });
          return { autoConnectSessionIds: [...state.autoConnectSessionIds, sessionId] };
        });
      },

      disableAutoConnect: (sessionId) => {
        set((state) => {
          if (!state.autoConnectSessionIds.includes(sessionId)) return state;
          console.info(`${SERVICE_TAG} Disabling auto-connect`, { sessionId });
          return { autoConnectSessionIds: state.autoConnectSessionIds.filter((id) => id !== sessionId) };
        });
      }
    }),
    {
      name: 'dicecapture-sessions',
      partialize: (state) => ({
        sessions: state.sessions,
        migrationComplete: state.migrationComplete,
        v2MigrationComplete: state.v2MigrationComplete,
        lastConnectedSessionId: state.lastConnectedSessionId,
        autoConnectSessionIds: state.autoConnectSessionIds
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;

        // V1 migration: endpoints -> sessions
        if (!state.migrationComplete) {
          const migratedSessions = migrateEndpointsToSessions();

          if (migratedSessions.length > 0) {
            state.sessions = migratedSessions;
            console.info(
              `${SERVICE_TAG} V1 migration complete, ${migratedSessions.length} sessions loaded`
            );
          }
          state.migrationComplete = true;
        }

        // V2 migration: StarterSessionConfig/PlayerSessionConfig -> unified SessionConfig
        if (!state.v2MigrationComplete) {
          state.sessions = migrateSessionsToV2(state.sessions);
          state.v2MigrationComplete = true;
        }

        // V3 migration: seed autoConnectSessionIds from lastConnectedSessionId
        if (!state.autoConnectSessionIds) {
          state.autoConnectSessionIds = [];
        }
        if (state.autoConnectSessionIds.length === 0 && state.lastConnectedSessionId) {
          const sessionExists = state.sessions.some((s) => s.id === state.lastConnectedSessionId);
          if (sessionExists) {
            state.autoConnectSessionIds = [state.lastConnectedSessionId];
            console.info(`${SERVICE_TAG} V3 migration: seeded autoConnectSessionIds from lastConnectedSessionId`);
          }
        }
      }
    }
  )
);
